import { IMAGE_AND_METADATA_UPLOAD_OPTIONS } from 'utils/constants';
import { notification } from 'antd5';
import { FileUploader } from 'components/FileUploader';
import { useAccountContext } from 'utils/account/AccountContext';
import { useGetIngestUploadUrls } from 'utils/ingest';
import {
  useNotifyInspectionImageUploadSuccessMutation,
  useNotifyInspectionImageUploadErrorMutation,
} from 'types/atlas-graphql';
import { OnCompleteStatus } from 'components/FileUploader/Modal/FileUploadModal';
import { ModalActionButtons, Button } from 'components/ui';

interface SkycrawlerUploadProps {
  inspectionId: string;
  onComplete: () => void;
  onBack: () => void;
}

function isValidFilePath(filename: string) {
  // So long as it's a `.mcap` file, we should be good
  return filename.toLowerCase().includes('.mcap');
}

// FIXME: Ask for MR feedback: I initially just used the InspectionFileUploader component that is a wrapper around the FileUploader
// But I backed away from that because I ended up with about half of the inputs being empty or undefined with comments like "This isn't relevant"
// So I'm doing a more specific wrapper here.
export const SkycrawlerUpload: React.FunctionComponent<SkycrawlerUploadProps> = ({
  inspectionId,
  onComplete: onSuccessCallback,
  onBack,
}) => {
  const { customer } = useAccountContext();
  const organizationId = customer?.id;
  const description = 'FIXME inspection description';

  const { cancelTokenSource, getIngestUploadUrls } = useGetIngestUploadUrls();

  const [notifySuccess] = useNotifyInspectionImageUploadSuccessMutation();
  const [notifyError] = useNotifyInspectionImageUploadErrorMutation();

  const payloadType = IMAGE_AND_METADATA_UPLOAD_OPTIONS.SKYCRAWLER_LOG;

  const onUpload = () => {
    onSuccessCallback();
    notification.open({
      message: 'Inspection images currently processing.',
      description: 'You will receive a notification when each inspection has been processed.',
    });
  };

  const onComplete = ({ status }: { status: OnCompleteStatus }) => {
    if (status === 'CANCEL') {
      cancelTokenSource.cancel('Operation canceled by the user.');
      return;
    }

    if (status === 'SUCCESS') {
      onUpload();
      notifySuccess({
        variables: {
          input: {
            inspectionId: inspectionId,
            message: `Your images and metadata have been successfully uploaded to your inspection with id "${inspectionId}".`,
          },
        },
      });
    }

    if (status === 'ERROR') {
      notifyError({
        variables: {
          input: {
            inspectionId: inspectionId,
            message: `An error occurred while uploading images and metadata to your inspection with id "${inspectionId}".`,
          },
        },
      });
    }
  };

  const multiPartProps = {
    recordId: inspectionId,
    outputDataType: IMAGE_AND_METADATA_UPLOAD_OPTIONS.SKYCRAWLER_LOG,
  };

  const getUploadUrls = async (filePaths: any) => {
    if (!organizationId || !payloadType) {
      return undefined;
    }

    try {
      const { items } = await getIngestUploadUrls({
        dataType: payloadType,
        files: filePaths || [],
        recordId: inspectionId,
      });

      return items;
    } catch (e) {
      return undefined;
    }
  };

  return (
    <div>
      <p>
        A SkyCrawler log must have the .mcap file extension. Once the log file is uploaded, it will
        be processed and released automatically. Inspection images and data will not be manually
        editable after upload
      </p>
      <ModalActionButtons>
        <Button data-testid="inspection-upload-cancel-btn" onClick={onBack}>
          Back
        </Button>
        <FileUploader
          id="skycrawler-data-upload"
          label={'Upload File'}
          acceptedFileTypes={'.mcap'}
          multiple={false}
          directory={false}
          validation={isValidFilePath}
          onFileComplete={() => {}}
          onComplete={onComplete}
          action={getUploadUrls}
          multipartProps={multiPartProps}
        />
      </ModalActionButtons>
    </div>
  );
};
